import { useState, useEffect } from 'react';
import RabbitProgress from '../components/RabbitProgress';

interface Question {
  id: string;
  question: string;
  type: 'dropdown' | 'text' | 'radio';
  options?: string[];
}

interface TDSResult {
  status: 'complete';
  section: string;
  section_description: string;
  rate_individual_huf: string;
  rate_others: string;
  threshold: string;
  rate_without_pan: string;
  applicable_act: string;
  notes: string;
  act_2025_note: string;
}

const QUICK_EXAMPLES = [
  {
    label: 'Rent Payment',
    text: 'Monthly rent payment of Rs 55,000 to an Individual landlord by our company',
  },
  {
    label: 'Contractor Payment',
    text: 'Payment to contractor for construction work of Rs 1,50,000 by our Private Limited Company',
  },
  {
    label: 'Professional Fees',
    text: 'Payment of Rs 40,000 to a Chartered Accountant (Individual) for audit services by our company',
  },
];

function extractAmount(text: string): number | null {
  const patterns = [
    /Rs\.?\s*([\d,]+(?:\.\d+)?)/i,
    /₹\s*([\d,]+(?:\.\d+)?)/,
    /([\d,]+(?:\.\d+)?)\s*rupees/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
  }
  return null;
}

function parseRate(rateStr: string): number | null {
  if (!rateStr) return null;
  const match = rateStr.match(/([\d.]+)%/);
  return match ? parseFloat(match[1]) : null;
}

function extractThresholdAmount(thresholdStr: string): number | null {
  if (!thresholdStr) return null;
  const match = thresholdStr.match(/Rs\.?\s*([\d,]+)/i);
  return match ? parseInt(match[1].replace(/,/g, ''), 10) : null;
}

export default function TDSCalculator() {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TDSResult | null>(null);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showGuidance, setShowGuidance] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [manualAmount, setManualAmount] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('tds_search_history');
    if (stored) {
      try { setSearchHistory(JSON.parse(stored)); } catch {}
    }
  }, []);

  const saveToHistory = (text: string) => {
    const trimmed = text.trim();
    setSearchHistory(prev => {
      const updated = [trimmed, ...prev.filter(h => h !== trimmed)].slice(0, 5);
      localStorage.setItem('tds_search_history', JSON.stringify(updated));
      return updated;
    });
  };

  const handleAnalyse = async () => {
    if (!description.trim()) { setError('Please enter a payment description'); return; }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch('/api/tds/analyse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Analysis failed');

      if (data.status === 'need_more_info') {
        setQuestions(data.questions || []);
        setAnswers({});
        setShowModal(true);
      } else {
        setResult(data);
        saveToHistory(description);
        setManualAmount('');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleModalSubmit = async () => {
    setShowModal(false);
    setLoading(true);

    const answerContext: Record<string, string> = {};
    questions.forEach(q => {
      if (answers[q.id]) answerContext[q.question] = answers[q.id];
    });

    try {
      const response = await fetch('/api/tds/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, answers: answerContext }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Calculation failed');
      setResult(data);
      saveToHistory(description);
      setManualAmount('');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResult = () => {
    if (!result) return;
    const lines = [
      'TDS Details',
      '============',
      `Section: ${result.section} - ${result.section_description}`,
      `Rate (Individual/HUF): ${result.rate_individual_huf}`,
      `Rate (Others): ${result.rate_others}`,
      `Rate without PAN: ${result.rate_without_pan}`,
      `Threshold: ${result.threshold}`,
      `Applicable Act: ${result.applicable_act}`,
    ];
    if (result.notes) lines.push('', `Notes: ${result.notes}`);
    if (result.act_2025_note) lines.push('', `Act 2025: ${result.act_2025_note}`);
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const detectedAmount = extractAmount(description);
  const amountToUse = manualAmount
    ? parseFloat(manualAmount.replace(/,/g, ''))
    : detectedAmount;
  const primaryRate = result
    ? (parseRate(result.rate_individual_huf) ?? parseRate(result.rate_others))
    : null;
  const tdsAmount =
    amountToUse && primaryRate !== null ? (amountToUse * primaryRate) / 100 : null;
  const thresholdAmount = result ? extractThresholdAmount(result.threshold) : null;
  const isBelowThreshold = !!(amountToUse && thresholdAmount && amountToUse < thresholdAmount);

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <span>Tools</span>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>TDS Calculator</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
            <span style={{ fontSize: "36px" }}>🧮</span>
            <h1>TDS Calculator</h1>
          </div>
          <p className="ph-subtitle">AI-powered TDS section and rate finder for any payment type</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-12">

      <div className="bg-white rounded-2xl shadow-sm border p-8">

        {/* Collapsible guidance */}
        <div className="mb-5">
          <button
            onClick={() => setShowGuidance(g => !g)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition">
            💡 How to get accurate results {showGuidance ? '▲' : '▼'}
          </button>
          {showGuidance && (
            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-gray-700">
              <p className="font-semibold mb-2">For best results, mention:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li>Type of payment (salary, rent, professional fees, etc.)</li>
                <li>Amount of payment</li>
                <li>Status of payee (Individual, Company, Firm, etc.)</li>
                <li>Status of payer (Individual, Company, etc.)</li>
              </ul>
              <p className="mt-3 text-xs italic text-gray-500">
                Example: "Payment of Rs 50,000 to a doctor (Individual) for professional
                consultation by our Private Limited Company"
              </p>
            </div>
          )}
        </div>

        {/* Search history */}
        {searchHistory.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-gray-400 mb-2">Recent searches:</p>
            <div className="flex flex-wrap gap-2">
              {searchHistory.map((h, i) => (
                <button
                  key={i}
                  onClick={() => { setDescription(h); setResult(null); setError(''); }}
                  className="text-xs bg-gray-100 hover:bg-blue-50 hover:text-blue-700 text-gray-500 px-3 py-1 rounded-full transition max-w-xs truncate">
                  {h.length > 45 ? h.substring(0, 45) + '…' : h}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Describe the payment
        </label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe the payment... (e.g. Rs 50,000 rent paid to Individual landlord by our Private Limited Company)"
          className="w-full border border-gray-200 rounded-xl p-4 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={4}
        />
        <p className="text-xs text-gray-400 italic mt-1">
          For accurate results, mention: type of payment, amount, and status of payer &amp; payee
          (e.g. Individual, Company, Firm)
        </p>

        {/* Quick examples */}
        <div className="mt-3">
          <p className="text-xs text-gray-400 mb-2">Quick examples:</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => { setDescription(ex.text); setResult(null); setError(''); }}
                className="text-xs bg-gray-100 hover:bg-blue-50 hover:text-blue-700 text-gray-600 px-3 py-1.5 rounded-full transition font-medium">
                {ex.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleAnalyse}
          disabled={!description.trim() || loading}
          className="w-full mt-6 bg-blue-700 text-white py-3 rounded-xl font-semibold hover:bg-blue-800 transition disabled:opacity-50 text-lg flex items-center justify-center gap-2">
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Analysing...
            </>
          ) : (
            'Find TDS Section & Rate'
          )}
        </button>

        <RabbitProgress loading={loading} estimatedSeconds={10} />

        {/* Result */}
        {result && (
          <div className="mt-6 space-y-4">
            {/* Act applicability box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
              <span className="font-semibold">📋 Act Applicability:</span>{' '}
              Income Tax Act, 1961 applies for payments before 01-04-2026. Income Tax Act, 2025
              applies for payments on or after 01-04-2026.
            </div>

            {/* Below threshold message */}
            {isBelowThreshold && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 flex items-start gap-2">
                <span className="text-lg leading-none mt-0.5">✓</span>
                <span>
                  <span className="font-semibold">TDS not applicable</span> — payment is below
                  the threshold limit of {result.threshold}.
                </span>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-6 border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 text-lg">TDS Details</h3>
                <button
                  onClick={handleCopyResult}
                  className="text-xs bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded-lg transition flex items-center gap-1">
                  {copied ? '✓ Copied!' : '📋 Copy Result'}
                </button>
              </div>

              {/* Section — prominent */}
              <div className="bg-blue-700 text-white rounded-xl p-4 mb-4">
                <p className="text-xs uppercase font-semibold opacity-75 tracking-wide">Section</p>
                <p className="text-3xl font-bold mt-1">{result.section || 'N/A'}</p>
                {result.section_description && (
                  <p className="text-sm opacity-90 mt-1">{result.section_description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-xs text-gray-400 uppercase font-semibold">Rate (Individual/HUF)</p>
                  <p className="text-xl font-bold text-blue-700 mt-1">
                    {result.rate_individual_huf || 'N/A'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-xs text-gray-400 uppercase font-semibold">Rate (Others)</p>
                  <p className="text-xl font-bold text-blue-700 mt-1">
                    {result.rate_others || 'N/A'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-xs text-gray-400 uppercase font-semibold">Rate without PAN</p>
                  <p className="text-xl font-bold text-orange-600 mt-1">
                    {result.rate_without_pan || '20%'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-xs text-gray-400 uppercase font-semibold">Threshold Limit</p>
                  <p className="text-sm font-semibold text-gray-700 mt-1">
                    {result.threshold || 'N/A'}
                  </p>
                </div>
              </div>

              {/* TDS Calculation box */}
              <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">TDS Calculation</p>
                {!detectedAmount && (
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 block mb-1">
                      Enter payment amount (Rs)
                    </label>
                    <input
                      type="number"
                      value={manualAmount}
                      onChange={e => setManualAmount(e.target.value)}
                      placeholder="e.g. 50000"
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                {amountToUse && tdsAmount !== null && primaryRate !== null ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Amount</span>
                      <span className="font-medium">
                        Rs {amountToUse.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">TDS Rate (Individual/HUF)</span>
                      <span className="font-medium">{result.rate_individual_huf}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-1">
                      <span className="text-gray-600 font-medium">TDS Amount</span>
                      <span className="font-bold text-red-600">
                        Rs {tdsAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">Net Payment</span>
                      <span className="font-bold text-green-600">
                        Rs{' '}
                        {(amountToUse - tdsAmount).toLocaleString('en-IN', {
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                  </div>
                ) : amountToUse ? (
                  <p className="text-xs text-gray-400">
                    Rate is variable — refer to the applicable rate shown above.
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">
                    Enter amount above to see TDS calculation.
                  </p>
                )}
              </div>

              {result.notes && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <span className="font-semibold">Note:</span> {result.notes}
                  </p>
                </div>
              )}

              {result.act_2025_note && (
                <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="text-sm text-indigo-800">
                    <span className="font-semibold">📋 Income Tax Act 2025:</span>{' '}
                    {result.act_2025_note}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Follow-up Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none font-bold">
              ×
            </button>
            <h2 className="text-xl font-bold text-gray-800 mb-1">A few more details needed</h2>
            <p className="text-sm text-gray-500 mb-6">
              To give you the exact TDS rate, please answer the following:
            </p>

            <div className="space-y-5">
              {questions.map(q => (
                <div key={q.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {q.question}
                  </label>
                  {q.type === 'dropdown' && q.options && (
                    <select
                      value={answers[q.id] || ''}
                      onChange={e =>
                        setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))
                      }
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                      <option value="">Select an option...</option>
                      {q.options.map(opt => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                  {q.type === 'radio' && q.options && (
                    <div className="space-y-2">
                      {q.options.map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={q.id}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() =>
                              setAnswers(prev => ({ ...prev, [q.id]: opt }))
                            }
                            className="accent-blue-600"
                          />
                          <span className="text-sm text-gray-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {q.type === 'text' && (
                    <input
                      type="text"
                      value={answers[q.id] || ''}
                      onChange={e =>
                        setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))
                      }
                      placeholder="Type your answer..."
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={handleModalSubmit}
              disabled={loading}
              className="w-full mt-6 bg-blue-700 text-white py-3 rounded-xl font-semibold hover:bg-blue-800 transition disabled:opacity-50">
              Get TDS Details
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
